<script lang="ts">
  import { service } from "@frontend/services";
  import { GetCachedToken, me } from "@frontend/user";
  import { push } from "svelte-spa-router";
  import { onMount } from "svelte";
  import { formatDate, formatDateTime, formatMoney } from "./Formats";
  import { 
    type AuthenticatedRequestOfTransactionperuserRequest, 
    type TransactionperuserRequest,
    type ApiResultOfListOfTransactionperuserResponse,
    type TransactionperuserResponse,
    type User,
    type AuthenticatedRequest,
    type ApiResultOfListOfUser
  } from "@frontend/walletapi";
   import { translate, currentLanguage } from "../Translator";

  let transactionData: TransactionperuserResponse[] = [];
  let loading = false;
  let error: string | null = null;
  let successMessage: string | null = null;
  let fechaInicio = new Date().toISOString().split('T')[0];
  let fechaFin = new Date().toISOString().split('T')[0];
  
  onMount(async () => {
    if (!$me) {
      push("/user/Login");
      return;
    }
    // Cargar datos automáticamente al montar la página
    await transactionperuser();
  });
  
  async function transactionperuser() {
    loading = true;
    error = null;
    successMessage = null;

    try {
      const token = GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      // Crear fechas en zona horaria local para evitar conversión UTC
      const inicio = new Date(fechaInicio + 'T00:00:00');
      const fin = new Date(fechaFin + 'T23:59:59');


      const response = await service.walletapi.transactionperuser({
        Token: token,
        Data: {
          UserId: undefined,
          startdate: inicio,
          enddate: fin
        }
      });

      if (response?.Success && response.Data) {
        transactionData = response.Data;
        successMessage = null;
        console.log('Transaction data loaded:', transactionData);
      } else {
        error = response?.ErrorMessage || 'No se pudieron cargar los datos de transacciones';
        transactionData = [];
      }
    } catch (err) {
      error = 'Error al cargar datos de transacciones';
      transactionData = [];
      console.error("Error loading transaction data:", err);
    } finally {
      loading = false;
    }
  }

  


  function goBack() {
    push("/PlayerList");
  }
</script>

<div class="ui container">
  <div class="ui basic segment">
    <button class="ui labeled icon button" on:click={goBack}>
      <i class="arrow left icon"></i>
      {$translate('Back to Player List')}
    </button>
  </div>

  <div class="ui segment">
    <h3 class="ui header">
      <i class="chart line icon"></i>
      <div class="content">
         {$translate('Transactions per User')}
        <div class="sub header">{$translate('View transaction summary for all users')}</div>
      </div>
    </h3>

    <!-- Date Range Selection -->
    <div class="ui form">
      <div class="two fields">
        <div class="field">
          <label>{$translate('Start Date')}</label>
          <input type="date" bind:value={fechaInicio} />
        </div>
        <div class="field">
          <label>{$translate('End Date')}</label>
          <input type="date" bind:value={fechaFin} />
        </div>
      </div>

      <div class="field">
        <button 
          class="ui primary button" 
          on:click={transactionperuser}
          disabled={!fechaInicio || !fechaFin || loading}
        >
          <i class="search icon"></i>
          {$translate('Upload Transaction Data')}
        </button>
      </div>
    </div>
  </div>

  {#if loading}
    <div class="ui active inverted dimmer">
      <div class="ui text loader">{$translate('Loading transaction data...')}</div>
    </div>
  {:else if error}
    <div class="ui negative message">
      <i class="close icon"></i>
      <div class="header">{$translate('Error')}</div>
      <p>{error}</p>
    </div>
  {:else if successMessage}
    <div class="ui positive message">
      <i class="checkmark icon"></i>
      <div class="header">{$translate('Success')}</div>
      <p>{successMessage}</p>
    </div>
  {/if}

  {#if transactionData && transactionData.length > 0}
    <div class="ui segment">
      <h4 class="ui header">
        <i class="users icon"></i>
        Resumen de Transacciones por Usuario
      </h4>
      
      <div class="ui table-container">
        <table class="ui celled table">
          <thead>
            <tr>
              <th>{$translate('User')}</th>
              <th>Id</th>
              <th>{$translate('Deposit')}</th>
              <th>{$translate('Withdraw')}</th>
              <th>{$translate('Deposit Promo')}</th>
              <th>{$translate('Withdraw Promo')}</th>
              <!-- <th>Retiros Canjeables</th> -->
            </tr>
          </thead>
          <tbody>
            {#each transactionData as userData}
              <tr>
                <td>
                  <div class="ui header">
                    <i class="user icon"></i>
                    {userData.FirstName}
                  </div>
                </td>
                <td>
                  <span class="ui label">{userData.UserId}</span>
                </td>
                <td class="positive">
                  <i class="arrow up icon"></i>
                  {formatMoney(userData.Mdeposit?.InCents || 0)}
                </td>
                <td class="negative">
                  <i class="arrow down icon"></i>
                  {formatMoney(userData.Mwithdraw?.InCents || 0)}
                </td>
                <td class="info">
                  <i class="gift icon"></i>
                  {formatMoney(userData.Pdeposit?.InCents || 0)}
                </td>
                <td class="warning">
                  <i class="gift icon"></i>
                  {formatMoney(userData.Pwithdraw?.InCents || 0)}
                </td>
              </tr>
            {/each}
            
            <!-- Fila de Totales -->
            <tr class="totals-row">
              <td>
                <div class="ui header">
                  <strong>Total</strong>
                </div>
              </td>
              <td>
                <span class="ui label">
                  {transactionData.length} {$translate('User')}
                </span>
              </td>
              <td class="positive total-cell">
                <i class="arrow up icon"></i>
                <strong>{formatMoney(transactionData.reduce((sum, user) => sum + (user.Mdeposit?.InCents || 0), 0))}</strong>
              </td>
              <td class="negative total-cell">
                <i class="arrow down icon"></i>
                <strong>{formatMoney(transactionData.reduce((sum, user) => sum + (user.Mwithdraw?.InCents || 0), 0))}</strong>
              </td>
              <td class="info total-cell">
                <i class="gift icon"></i>
                <strong>{formatMoney(transactionData.reduce((sum, user) => sum + (user.Pdeposit?.InCents || 0), 0))}</strong>
              </td>
              <td class="warning total-cell">
                <i class="gift icon"></i>
                <strong>{formatMoney(transactionData.reduce((sum, user) => sum + (user.Pwithdraw?.InCents || 0), 0))}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  {:else if transactionData && transactionData.length === 0}
    <div class="ui placeholder segment">
      <div class="ui icon header">
        <i class="search icon"></i>
       {$translate('No transactions were found for the selected date range.')}
      </div>
    </div>
  {/if}
</div>

<style>
  .ui.container {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 1rem !important;
  }

  .ui.segment {
    width: 100% !important;
    max-width: 100% !important;
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }

  .ui.form {
    width: 100% !important;
    max-width: 100% !important;
  }

  .ui.table-container {
    overflow-x: auto !important;
    margin: 1em 0 !important;
  }

  .ui.celled.table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  .ui.celled.table th {
    background-color: #f8f9fa !important;
    font-weight: bold !important;
    text-align: center !important;
    padding: 1em !important;
    border: 1px solid #dee2e6 !important;
  }

  .ui.celled.table td {
    padding: 1em !important;
    border: 1px solid #dee2e6 !important;
    text-align: center !important;
    vertical-align: middle !important;
  }

  .ui.celled.table td.positive {
    background-color: #d4edda !important;
    color: #155724 !important;
  }

  .ui.celled.table td.negative {
    background-color: #f8d7da !important;
    color: #721c24 !important;
  }

  .ui.celled.table td.info {
    background-color: #d1ecf1 !important;
    color: #0c5460 !important;
  }

  .ui.celled.table td.warning {
    background-color: #fff3cd !important;
    color: #856404 !important;
  }



  .ui.celled.table .ui.label {
    font-size: 0.9em !important;
  }

  .ui.celled.table .ui.header {
    margin: 0 !important;
    font-size: 1em !important;
  }

  .ui.celled.table .totals-row {
    background-color: #f8f9fa !important;
    border-top: 3px solid #007bff !important;
  }

  .ui.celled.table .totals-row td {
    font-weight: bold !important;
    background-color: #f8f9fa !important;
    border-top: 2px solid #007bff !important;
    font-size: 0.95em !important;
  }

  .ui.celled.table .total-cell {
    font-size: 1em !important;
    font-weight: bold !important;
  }

  .ui.celled.table .totals-row .ui.label {
    background-color: #007bff !important;
    color: white !important;
    font-weight: bold !important;
  }

  @media (max-width: 768px) {
    .ui.table-container {
      font-size: 0.8em !important;
    }
    
    .ui.celled.table th,
    .ui.celled.table td {
      padding: 0.5em !important;
    }
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .ui.container {
      padding: 0.5rem !important;
    }

    .ui.segment {
      padding: 0.8rem !important;
      margin: 0.5rem 0 !important;
    }

  }
</style>

